import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Link as LinkIcon, Users, Tag, Send, Plus, Trash2, ExternalLink,
  Sparkles, Mail, Loader2, Ticket, Megaphone, Zap, Pencil, Image as ImageIcon,
  Upload, Lightbulb, ShieldAlert, CheckCheck, UserCheck, Inbox, Eye, Search,
  ChevronDown,
} from 'lucide-react';
import AdminOpportunities from './AdminOpportunities';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const SECTION_TABS = [
  { key: 'links', label: 'Landing Pages', icon: LinkIcon },
  { key: 'leads', label: 'Lead Gen + Email', icon: Users },
  { key: 'spam', label: 'Spam Queue', icon: ShieldAlert },
  { key: 'deals', label: 'Deals & Retention', icon: Sparkles },
  { key: 'opportunities', label: 'Opportunities', icon: Lightbulb },
  { key: 'portfolio', label: 'Portfolio Photos', icon: ImageIcon },
  { key: 'sent-emails', label: 'Sent Emails', icon: Inbox },
];

const AdminMarketing = () => {
  const [section, setSection] = useState('links');

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="admin-marketing">
      <div className="mb-1">
        <h1 className="text-3xl font-bold text-gray-900">Marketing</h1>
        <p className="text-sm text-gray-500 mt-1">
          Landing pages · lead capture · email campaigns · promos · portfolio photos
        </p>
      </div>

      <div className="flex gap-2 mt-6 mb-6 border-b">
        {SECTION_TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setSection(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              section === t.key
                ? 'border-purple-600 text-purple-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
            data-testid={`marketing-tab-${t.key}`}
          >
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {section === 'links' && <LandingLinksSection />}
      {section === 'leads' && <LeadsSection />}
      {section === 'spam' && <SpamQueueSection />}
      {section === 'deals' && <DealsSection />}
      {section === 'opportunities' && (
        <div data-testid="marketing-opportunities">
          <AdminOpportunities />
        </div>
      )}
      {section === 'portfolio' && <PortfolioPhotosSection />}
      {section === 'sent-emails' && <SentEmailsSection />}
    </div>
  );
};

// ============================================================
// SECTION 1: Landing-page link manager
// ============================================================

const LandingLinksSection = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {id?, name, url, tags, notes}
  const [filterTag, setFilterTag] = useState('');
  // Embed-form modal — holds the snippet for whichever landing link's
  // "Embed form" button was clicked.
  const [embedModal, setEmbedModal] = useState(null); // {link_name, snippet}

  const fetchLinks = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/marketing/landing-links`, auth());
      setLinks(res.data.links || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchLinks(); }, []);

  const handleSave = async () => {
    if (!modal?.name?.trim() || !modal?.url?.trim()) {
      toast.error('Name and URL are required'); return;
    }
    const tags = (modal.tags || '').split(',').map(t => t.trim()).filter(Boolean);
    try {
      if (modal.id) {
        await axios.put(`${API}/admin/marketing/landing-links/${modal.id}`,
          { name: modal.name.trim(), url: modal.url.trim(), tags, notes: modal.notes || '' }, auth());
        toast.success('Link updated');
      } else {
        await axios.post(`${API}/admin/marketing/landing-links`,
          { name: modal.name.trim(), url: modal.url.trim(), tags, notes: modal.notes || '' }, auth());
        toast.success('Link added');
      }
      setModal(null); fetchLinks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this link?')) return;
    try {
      await axios.delete(`${API}/admin/marketing/landing-links/${id}`, auth());
      toast.success('Link deleted'); fetchLinks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  // Build the embed snippet locally (no server round trip needed —
  // backend route exists for QA, but for the copy modal we just
  // template the same HTML the backend would return). Pre-fills the
  // POST endpoint with this app's REACT_APP_BACKEND_URL so the snippet
  // works wherever it gets pasted.
  const buildEmbedSnippet = (link) => {
    const endpoint = `${BACKEND_URL}/api/public/marketing/leads`;
    const safeName = (link.name || 'Landing Page').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return `<!-- Local Flyer Delivery — Lead capture form -->
<!-- Bound to landing page: ${safeName} -->
<div class="lfd-lead-form" style="max-width:420px;font-family:system-ui,-apple-system,Segoe UI,sans-serif;">
  <form id="lfdLeadForm" novalidate>
    <input type="hidden" name="capture_token" value="${link.capture_token}" />
    <div style="margin-bottom:12px;">
      <label style="display:block;font-size:13px;color:#374151;margin-bottom:4px;">Your name</label>
      <input name="name" type="text" maxlength="200" required style="width:100%;padding:10px 12px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;" />
    </div>
    <div style="margin-bottom:12px;">
      <label style="display:block;font-size:13px;color:#374151;margin-bottom:4px;">Email</label>
      <input name="email" type="email" maxlength="200" required style="width:100%;padding:10px 12px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;" />
    </div>
    <div style="margin-bottom:12px;">
      <label style="display:block;font-size:13px;color:#374151;margin-bottom:4px;">Phone (optional)</label>
      <input name="phone" type="tel" maxlength="40" style="width:100%;padding:10px 12px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;" />
    </div>
    <div style="margin-bottom:16px;">
      <label style="display:block;font-size:13px;color:#374151;margin-bottom:4px;">Anything you'd like us to know?</label>
      <textarea name="message" maxlength="2000" rows="3" style="width:100%;padding:10px 12px;border:1px solid #d1d5db;border-radius:6px;font-size:14px;resize:vertical;"></textarea>
    </div>
    <input type="text" name="_hp" tabindex="-1" autocomplete="off" style="position:absolute;left:-9999px;top:-9999px;height:0;width:0;opacity:0;" aria-hidden="true" />
    <button type="submit" style="width:100%;padding:12px;background:#7c3aed;color:#fff;border:0;border-radius:6px;font-size:15px;font-weight:600;cursor:pointer;">Get in touch</button>
    <p id="lfdLeadFormStatus" style="margin-top:10px;font-size:13px;text-align:center;color:#6b7280;"></p>
  </form>
</div>
<script>
(function(){var f=document.getElementById('lfdLeadForm');var s=document.getElementById('lfdLeadFormStatus');if(!f)return;f.addEventListener('submit',function(e){e.preventDefault();s.style.color='#6b7280';s.textContent='Sending\\u2026';var d={};new FormData(f).forEach(function(v,k){d[k]=v;});fetch('${endpoint}',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(d)}).then(function(r){if(!r.ok)throw new Error('http '+r.status);return r.json();}).then(function(){s.style.color='#10b981';s.textContent='Thanks \\u2014 we\\u2019ll be in touch shortly.';f.reset();}).catch(function(){s.style.color='#dc2626';s.textContent='Something went wrong \\u2014 please try again.';});});})();
</script>`;
  };

  const handleRotateToken = async (linkId) => {
    if (!window.confirm(
      'Rotate the capture token?\n\n' +
      'Any embed snippet using the old token will stop working. Use this if a token leaks or you want to retire a campaign.'
    )) return;
    try {
      await axios.post(`${API}/admin/marketing/landing-links/${linkId}/rotate-token`, {}, auth());
      toast.success('Capture token rotated — re-copy the embed snippet.');
      fetchLinks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Rotate failed'); }
  };

  const allTags = [...new Set(links.flatMap(l => l.tags || []))];
  const filtered = filterTag ? links.filter(l => (l.tags || []).includes(filterTag)) : links;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs uppercase tracking-wide text-gray-500 mr-1">Filter:</span>
          <button
            onClick={() => setFilterTag('')}
            className={`px-2.5 py-1 rounded-full text-xs font-medium border ${
              !filterTag
                ? 'bg-purple-100 text-purple-700 border-purple-300'
                : 'bg-white text-gray-500 border-gray-200'
            }`}
          >
            All ({links.length})
          </button>
          {allTags.map(t => (
            <button
              key={t}
              onClick={() => setFilterTag(t === filterTag ? '' : t)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border ${
                filterTag === t
                  ? 'bg-purple-100 text-purple-700 border-purple-300'
                  : 'bg-white text-gray-500 border-gray-200'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <Button
          onClick={() => setModal({ name: '', url: '', tags: '', notes: '' })}
          className="bg-purple-600 hover:bg-purple-500"
          data-testid="add-link-btn"
        >
          <Plus size={16} className="mr-1" /> Add link
        </Button>
      </div>

      {loading ? (
        <div className="p-12 flex justify-center"><Loader2 className="animate-spin text-purple-500" /></div>
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center">
          <LinkIcon size={36} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-600 font-medium">No landing pages yet</p>
          <p className="text-xs text-gray-400 mt-1">
            Paste a Webflow / Carrd / wix URL above to start tracking it here.
          </p>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(l => (
            <Card key={l.id} className="p-4 flex items-start gap-3" data-testid={`link-card-${l.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-gray-900">{l.name}</p>
                  {(l.tags || []).map(t => (
                    <Badge key={t} className="bg-blue-50 text-blue-700 border border-blue-200 text-[10px]">
                      <Tag size={9} className="mr-0.5" />{t}
                    </Badge>
                  ))}
                </div>
                <a
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-purple-600 hover:underline inline-flex items-center gap-1 mt-0.5 truncate max-w-full"
                >
                  {l.url} <ExternalLink size={12} />
                </a>
                {l.notes && <p className="text-xs text-gray-500 mt-1">{l.notes}</p>}
              </div>
              <div className="flex gap-1">
                <Button
                  size="sm" variant="outline"
                  onClick={() => setEmbedModal({
                    link_id: l.id,
                    link_name: l.name,
                    snippet: buildEmbedSnippet(l),
                  })}
                  className="text-purple-600 border-purple-200 hover:bg-purple-50"
                  data-testid={`embed-link-${l.id}`}
                  title="Get the HTML form snippet for this landing page"
                ><Megaphone size={14} className="mr-1" /> Embed form</Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => setModal({
                    id: l.id, name: l.name, url: l.url,
                    tags: (l.tags || []).join(', '), notes: l.notes || '',
                  })}
                ><Pencil size={14} /></Button>
                <Button
                  size="sm" variant="outline"
                  onClick={() => handleDelete(l.id)}
                  className="text-red-500 border-red-200 hover:bg-red-50"
                ><Trash2 size={14} /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!modal} onOpenChange={(open) => !open && setModal(null)}>
        <DialogContent data-testid="link-modal">
          <DialogHeader>
            <DialogTitle>{modal?.id ? 'Edit Landing Page' : 'Add Landing Page'}</DialogTitle>
          </DialogHeader>
          {modal && (
            <div className="space-y-3">
              <div>
                <Label>Name *</Label>
                <Input
                  value={modal.name}
                  onChange={(e) => setModal({ ...modal, name: e.target.value })}
                  placeholder="e.g. Houston Q2 Promo Landing"
                  data-testid="link-modal-name"
                />
              </div>
              <div>
                <Label>URL *</Label>
                <Input
                  value={modal.url}
                  onChange={(e) => setModal({ ...modal, url: e.target.value })}
                  placeholder="https://yourcampaign.com/houston-q2"
                  data-testid="link-modal-url"
                />
              </div>
              <div>
                <Label>Tags <span className="text-gray-400 font-normal">(comma-separated)</span></Label>
                <Input
                  value={modal.tags}
                  onChange={(e) => setModal({ ...modal, tags: e.target.value })}
                  placeholder="houston, q2-2026, restaurants"
                  data-testid="link-modal-tags"
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  rows={3}
                  value={modal.notes}
                  onChange={(e) => setModal({ ...modal, notes: e.target.value })}
                  placeholder="Internal notes — which agency built it, what audience, etc."
                  data-testid="link-modal-notes"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setModal(null)}>Cancel</Button>
                <Button
                  onClick={handleSave}
                  className="bg-purple-600 hover:bg-purple-500"
                  data-testid="link-modal-save"
                >Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!embedModal} onOpenChange={(open) => !open && setEmbedModal(null)}>
        <DialogContent className="max-w-2xl" data-testid="embed-modal">
          <DialogHeader>
            <DialogTitle>Embed lead form — {embedModal?.link_name}</DialogTitle>
          </DialogHeader>
          {embedModal && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500">
                Paste this HTML into your landing page (Webflow embed block, Carrd HTML widget, etc.). Submissions land in the Leads tab tagged with the landing page name.
              </p>
              <Textarea
                rows={14}
                readOnly
                value={embedModal.snippet}
                className="font-mono text-xs"
                data-testid="embed-modal-snippet"
                onFocus={(e) => e.target.select()}
              />
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => handleRotateToken(embedModal.link_id)}
                  className="text-amber-600 border-amber-200 hover:bg-amber-50"
                  data-testid="embed-modal-rotate"
                  title="Generate a new capture token (old snippets stop working)"
                >Rotate token</Button>
                <Button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(embedModal.snippet);
                      toast.success('Snippet copied to clipboard');
                    } catch (e) { toast.error('Copy failed — please select + copy manually'); }
                  }}
                  className="bg-purple-600 hover:bg-purple-500"
                  data-testid="embed-modal-copy"
                >Copy snippet</Button>
                <Button variant="outline" onClick={() => setEmbedModal(null)}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================
// SECTION 2: Lead generation + drip sequences
// ============================================================

const LeadServiceBadge = ({ label, value, testId }) => {
  const v = (value || '').toString().trim();
  const yes = /^(y|true|1)/i.test(v) || /yes/i.test(v);
  const cls = !v
    ? 'bg-gray-100 text-gray-400 border border-gray-200'
    : yes
      ? 'bg-green-100 text-green-700 border border-green-300 font-semibold'
      : 'bg-slate-100 text-slate-600 border border-slate-200';
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs ${cls}`} data-testid={testId}>
      {label}: {v || '—'}
    </span>
  );
};

const LeadDetailField = ({ label, value }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wide text-gray-400">{label}</p>
    <p className="text-sm text-gray-800">{value || '—'}</p>
  </div>
);

const LeadsSection = () => {
  const [view, setView] = useState('leads'); // leads | sequences
  const [leads, setLeads] = useState([]);
  const [expandedLeadId, setExpandedLeadId] = useState(null);
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(false);

  const [leadModal, setLeadModal] = useState(null);
  const [seqModal, setSeqModal] = useState(null);
  const [enrollModal, setEnrollModal] = useState(null); // { sequence_id, sequence_name }
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        axios.get(`${API}/admin/marketing/leads`, auth()),
        axios.get(`${API}/admin/marketing/sequences`, auth()),
      ]);
      setLeads(l.data.leads || []);
      setSequences(s.data.sequences || []);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleSaveLead = async () => {
    if (!leadModal?.email?.trim()) { toast.error('Email required'); return; }
    const tags = (leadModal.tags || '').split(',').map(t => t.trim()).filter(Boolean);
    try {
      await axios.post(`${API}/admin/marketing/leads`, {
        email: leadModal.email.trim().toLowerCase(),
        name: leadModal.name || '',
        source: leadModal.source || 'manual',
        tags,
        notes: leadModal.notes || '',
      }, auth());
      toast.success('Lead saved');
      setLeadModal(null); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const handleDeleteLead = async (id) => {
    if (!window.confirm('Delete this lead and any active sequence enrollments?')) return;
    try {
      await axios.delete(`${API}/admin/marketing/leads/${id}`, auth());
      toast.success('Lead deleted'); fetchAll();
    } catch (e) { toast.error('Delete failed'); }
  };

  const handleSaveSequence = async () => {
    if (!seqModal?.name?.trim()) { toast.error('Name required'); return; }
    if (!seqModal.steps?.length) { toast.error('At least one step required'); return; }
    for (const s of seqModal.steps) {
      if (!s.subject?.trim() || !s.body_html?.trim()) {
        toast.error('Each step needs subject + body'); return;
      }
    }
    try {
      const payload = {
        name: seqModal.name.trim(),
        description: seqModal.description || '',
        steps: seqModal.steps.map(s => ({
          day_offset: Number(s.day_offset) || 0,
          subject: s.subject.trim(),
          body_html: s.body_html.trim(),
        })),
        active: !!seqModal.active,
      };
      if (seqModal.id) {
        await axios.put(`${API}/admin/marketing/sequences/${seqModal.id}`, payload, auth());
      } else {
        await axios.post(`${API}/admin/marketing/sequences`, payload, auth());
      }
      toast.success('Sequence saved'); setSeqModal(null); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const handleDeleteSequence = async (id) => {
    if (!window.confirm('Delete this sequence + all enrollments?')) return;
    try {
      await axios.delete(`${API}/admin/marketing/sequences/${id}`, auth());
      toast.success('Sequence deleted'); fetchAll();
    } catch (e) { toast.error('Delete failed'); }
  };

  const handleEnroll = async () => {
    if (!enrollModal || selectedLeadIds.length === 0) {
      toast.error('Pick at least one lead'); return;
    }
    try {
      const res = await axios.post(
        `${API}/admin/marketing/sequences/${enrollModal.sequence_id}/enroll`,
        { lead_ids: selectedLeadIds }, auth(),
      );
      toast.success(`Enrolled ${res.data.enrolled} (skipped ${res.data.skipped})`);
      setEnrollModal(null); setSelectedLeadIds([]);
    } catch (e) { toast.error(e.response?.data?.detail || 'Enroll failed'); }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setView('leads')}
          className={`px-3 py-1.5 rounded-md text-sm font-medium ${
            view === 'leads' ? 'bg-purple-100 text-purple-700' : 'text-gray-500 hover:bg-gray-50'
          }`}
          data-testid="leads-view-leads"
        >Leads ({leads.length})</button>
        <button
          onClick={() => setView('sequences')}
          className={`px-3 py-1.5 rounded-md text-sm font-medium ${
            view === 'sequences' ? 'bg-purple-100 text-purple-700' : 'text-gray-500 hover:bg-gray-50'
          }`}
          data-testid="leads-view-sequences"
        >Drip Sequences ({sequences.length})</button>
      </div>

      {/* ----- Leads list ----- */}
      {view === 'leads' && (
        <div>
          <div className="flex items-center justify-end mb-3">
            <Button
              onClick={() => setLeadModal({ email: '', name: '', source: 'manual', tags: '', notes: '' })}
              className="bg-purple-600 hover:bg-purple-500"
              data-testid="add-lead-btn"
            ><Plus size={16} className="mr-1" /> Add lead</Button>
          </div>
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="animate-spin inline text-purple-500" /></div>
          ) : leads.length === 0 ? (
            <Card className="p-10 text-center">
              <Users size={36} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-600 font-medium">No leads yet</p>
              <p className="text-xs text-gray-400 mt-1">Add leads manually or import via CSV later.</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {leads.map(l => {
                const fd = l.lead_form_data || {};
                const hasFormData = Object.values(fd).some(v => (v || '').toString().trim());
                const expanded = expandedLeadId === l.id;
                const fmtTs = (ts) => {
                  if (!ts) return '';
                  try {
                    return new Date(ts.endsWith?.('Z') ? ts : ts + 'Z').toLocaleString('en-US', {
                      timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric',
                      hour: 'numeric', minute: '2-digit',
                    }) + ' CT';
                  } catch { return ts; }
                };
                return (
                  <Card
                    key={l.id}
                    className="p-4 cursor-pointer hover:border-purple-200 transition-colors"
                    onClick={() => setExpandedLeadId(expanded ? null : l.id)}
                    data-testid={`lead-row-${l.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-gray-900">{l.name || l.email}</p>
                          <Badge className="bg-gray-100 text-gray-600 text-[10px] uppercase">{l.status}</Badge>
                          {(l.tags || []).map(t => (
                            <Badge key={t} className="bg-blue-50 text-blue-700 border border-blue-200 text-[10px]">{t}</Badge>
                          ))}
                          {hasFormData && (
                            <>
                              {/yes|^(y|true|1)/i.test((fd.design_needed || '').trim()) && (
                                <Badge className="bg-green-100 text-green-700 border border-green-300 text-[10px]">Design</Badge>
                              )}
                              {/yes|^(y|true|1)/i.test((fd.print_needed || '').trim()) && (
                                <Badge className="bg-green-100 text-green-700 border border-green-300 text-[10px]">Print</Badge>
                              )}
                            </>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{l.email} · source: {l.source}</p>
                      </div>
                      <ChevronDown
                        size={16}
                        className={`text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
                        data-testid={`lead-expand-chevron-${l.id}`}
                      />
                      <Button
                        size="sm" variant="outline"
                        onClick={(e) => { e.stopPropagation(); handleDeleteLead(l.id); }}
                        className="text-red-500 border-red-200 hover:bg-red-50"
                        data-testid={`lead-delete-btn-${l.id}`}
                      ><Trash2 size={14} /></Button>
                    </div>
                    {expanded && (
                      <div className="mt-3 pt-3 border-t border-gray-100" data-testid={`lead-details-${l.id}`}>
                        {hasFormData ? (
                          <>
                            <div className="flex flex-wrap gap-2 mb-3">
                              <LeadServiceBadge label="Design Services" value={fd.design_needed} testId={`lead-design-badge-${l.id}`} />
                              <LeadServiceBadge label="Print Services" value={fd.print_needed} testId={`lead-print-badge-${l.id}`} />
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3">
                              <LeadDetailField label="Business" value={fd.business_name} />
                              <LeadDetailField label="Phone" value={fd.phone} />
                              <LeadDetailField label="Delivery Quantity" value={fd.delivery_quantity} />
                              <LeadDetailField label="Delivery Date" value={fd.delivery_date} />
                              <LeadDetailField label="Target Area" value={fd.target_area} />
                              <LeadDetailField label="Form Submitted" value={fmtTs(l.last_form_submission_at)} />
                              <LeadDetailField label="Delivery Area Map" value={l.intake_polygon ? 'Drawn by customer' : 'Not drawn yet'} />
                            </div>
                          </>
                        ) : (
                          <p className="text-sm text-gray-400 italic" data-testid={`lead-no-form-data-${l.id}`}>
                            No form submission details for this lead{l.source === 'manual' ? ' (added manually)' : ''}.
                          </p>
                        )}
                        {l.notes && (
                          <p className="text-xs text-gray-500 mt-3"><span className="font-medium text-gray-600">Notes:</span> {l.notes}</p>
                        )}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ----- Sequences list ----- */}
      {view === 'sequences' && (
        <div>
          <Card className="p-3 mb-3 bg-amber-50 border-amber-200">
            <p className="text-xs text-amber-800">
              <Zap size={12} className="inline mr-1" />
              <b>MVP:</b> sequences are stored as templates. To fire a step, enroll leads and click
              <i> Send step now </i> on each enrollment. A scheduled worker (auto-fire by day) lives on the P2 backlog.
            </p>
          </Card>
          <div className="flex justify-end mb-3">
            <Button
              onClick={() => setSeqModal({
                name: '', description: '', active: true,
                steps: [{ day_offset: 0, subject: '', body_html: '' }],
              })}
              className="bg-purple-600 hover:bg-purple-500"
              data-testid="add-sequence-btn"
            ><Plus size={16} className="mr-1" /> New sequence</Button>
          </div>

          {sequences.length === 0 ? (
            <Card className="p-10 text-center">
              <Mail size={36} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-600 font-medium">No sequences yet</p>
              <p className="text-xs text-gray-400 mt-1">
                Create a multi-step email sequence (Day 0 welcome → Day 3 case study → Day 7 offer).
              </p>
            </Card>
          ) : (
            <div className="space-y-2">
              {sequences.map(s => (
                <Card key={s.id} className="p-4" data-testid={`sequence-row-${s.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">{s.name}</p>
                      <p className="text-xs text-gray-500">
                        {(s.steps || []).length} step(s) · {s.active ? 'Active' : 'Paused'}
                      </p>
                      {s.description && <p className="text-sm text-gray-500 mt-1">{s.description}</p>}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm" variant="outline"
                        onClick={() => { setEnrollModal({ sequence_id: s.id, sequence_name: s.name }); setSelectedLeadIds([]); }}
                        data-testid={`enroll-sequence-${s.id}`}
                      ><Send size={14} className="mr-1" /> Enroll</Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => setSeqModal({
                          id: s.id, name: s.name, description: s.description || '',
                          active: s.active, steps: s.steps || [],
                        })}
                      ><Pencil size={14} /></Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => handleDeleteSequence(s.id)}
                        className="text-red-500 border-red-200 hover:bg-red-50"
                      ><Trash2 size={14} /></Button>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t flex flex-wrap gap-2">
                    {(s.steps || []).map((step, idx) => (
                      <Badge key={idx} className="bg-purple-50 text-purple-700 border border-purple-200">
                        Day {step.day_offset}: {step.subject?.slice(0, 40) || '(no subject)'}
                      </Badge>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ----- Lead modal ----- */}
      <Dialog open={!!leadModal} onOpenChange={(open) => !open && setLeadModal(null)}>
        <DialogContent data-testid="lead-modal">
          <DialogHeader><DialogTitle>Add Lead</DialogTitle></DialogHeader>
          {leadModal && (
            <div className="space-y-3">
              <div><Label>Email *</Label><Input value={leadModal.email} onChange={(e) => setLeadModal({ ...leadModal, email: e.target.value })} /></div>
              <div><Label>Name</Label><Input value={leadModal.name} onChange={(e) => setLeadModal({ ...leadModal, name: e.target.value })} /></div>
              <div><Label>Source</Label><Input value={leadModal.source} onChange={(e) => setLeadModal({ ...leadModal, source: e.target.value })} placeholder="manual / landing / referral" /></div>
              <div><Label>Tags (comma-sep)</Label><Input value={leadModal.tags} onChange={(e) => setLeadModal({ ...leadModal, tags: e.target.value })} /></div>
              <div><Label>Notes</Label><Textarea rows={2} value={leadModal.notes} onChange={(e) => setLeadModal({ ...leadModal, notes: e.target.value })} /></div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setLeadModal(null)}>Cancel</Button>
                <Button onClick={handleSaveLead} className="bg-purple-600 hover:bg-purple-500">Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ----- Sequence modal ----- */}
      <Dialog open={!!seqModal} onOpenChange={(open) => !open && setSeqModal(null)}>
        <DialogContent className="max-w-2xl" data-testid="sequence-modal">
          <DialogHeader><DialogTitle>{seqModal?.id ? 'Edit' : 'New'} Drip Sequence</DialogTitle></DialogHeader>
          {seqModal && (
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              <div><Label>Name *</Label><Input value={seqModal.name} onChange={(e) => setSeqModal({ ...seqModal, name: e.target.value })} placeholder="e.g. New Client Welcome Sequence" /></div>
              <div><Label>Description</Label><Input value={seqModal.description} onChange={(e) => setSeqModal({ ...seqModal, description: e.target.value })} /></div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!seqModal.active}
                  onChange={(e) => setSeqModal({ ...seqModal, active: e.target.checked })}
                /> Active
              </label>
              <div className="border-t pt-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold">Steps</p>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => setSeqModal({
                      ...seqModal,
                      steps: [...seqModal.steps, { day_offset: (seqModal.steps[seqModal.steps.length - 1]?.day_offset || 0) + 3, subject: '', body_html: '' }],
                    })}
                  ><Plus size={14} className="mr-1" /> Add step</Button>
                </div>
                {seqModal.steps.map((s, idx) => (
                  <div key={idx} className="p-3 border rounded mb-2 space-y-2 bg-gray-50">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold">Step {idx + 1}</span>
                      <span className="text-xs text-gray-500">· Day</span>
                      <Input
                        type="number" min={0} max={180} className="w-20 h-8"
                        value={s.day_offset}
                        onChange={(e) => {
                          const steps = [...seqModal.steps];
                          steps[idx] = { ...s, day_offset: Number(e.target.value) || 0 };
                          setSeqModal({ ...seqModal, steps });
                        }}
                      />
                      <div className="flex-1" />
                      {seqModal.steps.length > 1 && (
                        <Button
                          size="sm" variant="outline"
                          onClick={() => {
                            setSeqModal({ ...seqModal, steps: seqModal.steps.filter((_, i) => i !== idx) });
                          }}
                          className="text-red-500 border-red-200 hover:bg-red-50 h-8 w-8 p-0"
                        ><Trash2 size={12} /></Button>
                      )}
                    </div>
                    <Input
                      placeholder="Subject line"
                      value={s.subject}
                      onChange={(e) => {
                        const steps = [...seqModal.steps];
                        steps[idx] = { ...s, subject: e.target.value };
                        setSeqModal({ ...seqModal, steps });
                      }}
                    />
                    <Textarea
                      rows={4}
                      placeholder="HTML body — <p>Hi {name},</p>"
                      value={s.body_html}
                      onChange={(e) => {
                        const steps = [...seqModal.steps];
                        steps[idx] = { ...s, body_html: e.target.value };
                        setSeqModal({ ...seqModal, steps });
                      }}
                    />
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setSeqModal(null)}>Cancel</Button>
                <Button onClick={handleSaveSequence} className="bg-purple-600 hover:bg-purple-500" data-testid="sequence-save">Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ----- Enroll modal ----- */}
      <Dialog open={!!enrollModal} onOpenChange={(open) => !open && setEnrollModal(null)}>
        <DialogContent data-testid="enroll-modal">
          <DialogHeader><DialogTitle>Enroll leads in "{enrollModal?.sequence_name}"</DialogTitle></DialogHeader>
          {enrollModal && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500">Pick the leads to enroll. Each lead's first step fires when you click "Send step now" on their enrollment.</p>
              <div className="max-h-80 overflow-y-auto border rounded">
                {leads.length === 0 ? (
                  <p className="p-4 text-sm text-gray-500 text-center">No leads — add some first.</p>
                ) : leads.map(l => (
                  <label key={l.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0">
                    <input
                      type="checkbox"
                      checked={selectedLeadIds.includes(l.id)}
                      onChange={(e) => setSelectedLeadIds(e.target.checked
                        ? [...selectedLeadIds, l.id]
                        : selectedLeadIds.filter(id => id !== l.id))}
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{l.name || l.email}</p>
                      <p className="text-xs text-gray-500">{l.email}</p>
                    </div>
                  </label>
                ))}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEnrollModal(null)}>Cancel</Button>
                <Button
                  onClick={handleEnroll}
                  className="bg-purple-600 hover:bg-purple-500"
                  disabled={selectedLeadIds.length === 0}
                  data-testid="enroll-confirm"
                >
                  Enroll {selectedLeadIds.length}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================
// SECTION 2.5: Spam Queue (false-positive recovery for the iter97
// 4-layer spam filter on /lead-intake + /leads)
// ============================================================

const REASON_BADGE_STYLES = {
  turnstile_token_missing: 'bg-red-100 text-red-800',
  turnstile_failed: 'bg-red-100 text-red-800',
  turnstile_verify_failed: 'bg-red-100 text-red-800',
  fill_time_too_short: 'bg-amber-100 text-amber-800',
  name_consonant_run: 'bg-purple-100 text-purple-800',
  name_no_vowels: 'bg-purple-100 text-purple-800',
  name_one_word_too_long: 'bg-purple-100 text-purple-800',
  name_alnum_run: 'bg-purple-100 text-purple-800',
  name_length_suspicious: 'bg-purple-100 text-purple-800',
  name_non_printable: 'bg-purple-100 text-purple-800',
  disposable_domain: 'bg-rose-100 text-rose-800',
};

const reasonStyle = (reason) => {
  const key = reason.split(':')[0]; // strip "disposable_domain:mailinator.com" → "disposable_domain"
  return REASON_BADGE_STYLES[key] || 'bg-gray-100 text-gray-700';
};

const SpamQueueSection = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [unreviewed, setUnreviewed] = useState(0);
  const [showReviewed, setShowReviewed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const fetchQueue = async () => {
    setLoading(true);
    try {
      const url = `${API}/admin/marketing/spam-leads${showReviewed ? '' : '?reviewed=false'}`;
      const res = await axios.get(url, auth());
      setRows(res.data.spam_leads || []);
      setTotal(res.data.total || 0);
      setUnreviewed(res.data.unreviewed_count || 0);
    } catch (e) {
      toast.error(`Failed to load spam queue: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchQueue(); /* eslint-disable-next-line */ }, [showReviewed]);

  const promote = async (row) => {
    if (!window.confirm(`Promote "${row.email || row.name}" to a real marketing lead?\n\nDo this only if you're sure this is a legitimate customer who got blocked by the spam filter.`)) return;
    setBusyId(row.id);
    try {
      const res = await axios.post(`${API}/admin/marketing/spam-leads/${row.id}/promote`, {}, auth());
      toast.success(res.data?.deduped
        ? `Already a lead — marked reviewed`
        : `Promoted to marketing lead`);
      fetchQueue();
    } catch (e) {
      toast.error(`Promote failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const markReviewed = async (row) => {
    setBusyId(row.id);
    try {
      await axios.post(`${API}/admin/marketing/spam-leads/${row.id}/mark-reviewed`, {}, auth());
      toast.success('Marked reviewed');
      fetchQueue();
    } catch (e) {
      toast.error(`Mark-reviewed failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="marketing-spam-queue">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldAlert size={20} className="text-amber-600" /> Spam Queue
            {unreviewed > 0 && (
              <Badge
                className="bg-amber-500 text-white text-xs"
                data-testid="spam-queue-unreviewed-count"
              >
                {unreviewed} unreviewed
              </Badge>
            )}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Submissions dropped by the 4-layer spam filter. Click "Promote" if a real customer slipped through; "Mark reviewed" to clear it from the queue.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowReviewed((v) => !v)}
          data-testid="spam-queue-toggle-reviewed"
        >
          {showReviewed ? 'Hide reviewed' : 'Show all (incl. reviewed)'}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-500">
          <Loader2 className="animate-spin mr-2" size={18} /> Loading spam queue…
        </div>
      ) : rows.length === 0 ? (
        <Card className="p-10 text-center text-gray-500">
          <CheckCheck size={28} className="mx-auto text-emerald-500 mb-2" />
          <p className="font-medium text-gray-700">
            {showReviewed ? 'No spam submissions yet.' : 'Queue is clear — nothing to review.'}
          </p>
          <p className="text-sm">
            Every form submission flagged by Turnstile, fill-time, name-sanity, or disposable-email check lands here.
          </p>
        </Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="spam-queue-table">
            <thead className="bg-gray-50 border-b text-left text-xs text-gray-600 uppercase">
              <tr>
                <th className="px-4 py-2.5">When</th>
                <th className="px-4 py-2.5">Name / Email</th>
                <th className="px-4 py-2.5">Source</th>
                <th className="px-4 py-2.5">Flagged for</th>
                <th className="px-4 py-2.5">IP</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className={`border-b last:border-0 ${r.reviewed ? 'bg-gray-50 text-gray-500' : 'hover:bg-amber-50/40'}`}
                  data-testid={`spam-row-${r.id}`}
                >
                  <td className="px-4 py-3 whitespace-nowrap text-xs">
                    {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3 min-w-[200px]">
                    <p className="font-medium text-gray-900 truncate max-w-[280px]" title={r.name}>
                      {r.name || <span className="text-gray-400 italic">(no name)</span>}
                    </p>
                    <p className="text-xs text-gray-500 truncate max-w-[280px]" title={r.email}>
                      {r.email || '(no email)'}
                    </p>
                    {r.phone && (
                      <p className="text-xs text-gray-400">{r.phone}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 max-w-[120px] truncate">
                    {r.source || '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1 max-w-[260px]">
                      {(r.spam_reasons || []).map((reason, i) => (
                        <Badge
                          key={i}
                          className={`${reasonStyle(reason)} text-[10px] py-0 px-1.5 font-mono`}
                        >
                          {reason.replace('_', ' ')}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 font-mono">
                    {r.capture_ip || '—'}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {r.reviewed ? (
                      <span className="text-xs text-gray-400 inline-flex items-center gap-1">
                        <CheckCheck size={12} /> reviewed
                      </span>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 mr-2"
                          disabled={busyId === r.id || !r.email}
                          onClick={() => promote(r)}
                          title={r.email ? 'Promote to marketing_leads' : 'Cannot promote — no email captured'}
                          data-testid={`spam-row-${r.id}-promote`}
                        >
                          <UserCheck size={12} className="mr-1" /> Promote
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-gray-600 hover:bg-gray-100"
                          disabled={busyId === r.id}
                          onClick={() => markReviewed(r)}
                          data-testid={`spam-row-${r.id}-mark-reviewed`}
                        >
                          Mark reviewed
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {total > rows.length && (
            <div className="px-4 py-3 text-xs text-gray-500 border-t bg-gray-50">
              Showing {rows.length} of {total} total. Older rows beyond 200 are truncated — adjust limit in the API if you need more.
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

// ============================================================
// SECTION 3: Deals + Retention
// ============================================================

const DealsSection = () => {
  const [view, setView] = useState('promos'); // promos | broadcast
  const [promos, setPromos] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [promoModal, setPromoModal] = useState(null);
  const [dealModal, setDealModal] = useState(null);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [sending, setSending] = useState(false);

  const fetchPromos = async () => {
    try {
      const res = await axios.get(`${API}/admin/marketing/promo-codes`, auth());
      setPromos(res.data.promos || []);
    } catch (e) {}
  };
  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/admin/clients`, auth());
      setClients(res.data || []);
    } catch (e) {}
  };

  useEffect(() => { fetchPromos(); fetchClients(); }, []);

  const handleSavePromo = async () => {
    if (!promoModal?.code?.trim()) { toast.error('Code required'); return; }
    try {
      await axios.post(`${API}/admin/marketing/promo-codes`, {
        code: promoModal.code.trim().toUpperCase(),
        description: promoModal.description || '',
        discount_type: promoModal.discount_type,
        discount_value: Number(promoModal.discount_value),
        max_uses: promoModal.max_uses ? Number(promoModal.max_uses) : null,
      }, auth());
      toast.success('Promo created'); setPromoModal(null); fetchPromos();
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
  };

  const handleDeletePromo = async (id) => {
    if (!window.confirm('Delete this promo code?')) return;
    try {
      await axios.delete(`${API}/admin/marketing/promo-codes/${id}`, auth());
      toast.success('Deleted'); fetchPromos();
    } catch (e) { toast.error('Delete failed'); }
  };

  const handleSendDeal = async () => {
    if (!dealModal?.subject?.trim() || !dealModal?.body_html?.trim()) {
      toast.error('Subject + body required'); return;
    }
    if (selectedClientIds.length === 0 && !dealModal.custom_emails?.trim()) {
      toast.error('Pick at least one recipient'); return;
    }
    setSending(true);
    try {
      const payload = selectedClientIds.length > 0
        ? {
          recipient_type: 'clients',
          recipient_ids: selectedClientIds,
          custom_emails: [],
          subject: dealModal.subject.trim(),
          body_html: dealModal.body_html,
          promo_code: dealModal.promo_code || null,
        }
        : {
          recipient_type: 'custom',
          recipient_ids: [],
          custom_emails: (dealModal.custom_emails || '').split(/[,\n]+/).map(s => s.trim()).filter(Boolean),
          subject: dealModal.subject.trim(),
          body_html: dealModal.body_html,
          promo_code: dealModal.promo_code || null,
        };
      const res = await axios.post(`${API}/admin/marketing/send-deal`, payload, auth());
      toast.success(`Sent to ${res.data.delivered}/${res.data.recipients} recipients (${res.data.failed} failed)`);
      setDealModal(null); setSelectedClientIds([]);
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setSending(false); }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setView('promos')}
          className={`px-3 py-1.5 rounded-md text-sm font-medium ${
            view === 'promos' ? 'bg-purple-100 text-purple-700' : 'text-gray-500 hover:bg-gray-50'
          }`}
          data-testid="deals-view-promos"
        >Promo Codes ({promos.length})</button>
        <button
          onClick={() => setView('broadcast')}
          className={`px-3 py-1.5 rounded-md text-sm font-medium ${
            view === 'broadcast' ? 'bg-purple-100 text-purple-700' : 'text-gray-500 hover:bg-gray-50'
          }`}
          data-testid="deals-view-broadcast"
        >Send a Deal</button>
      </div>

      {view === 'promos' && (
        <div>
          <div className="flex justify-end mb-3">
            <Button
              onClick={() => setPromoModal({ code: '', description: '', discount_type: 'percent', discount_value: 10, max_uses: '' })}
              className="bg-purple-600 hover:bg-purple-500"
              data-testid="add-promo-btn"
            ><Plus size={16} className="mr-1" /> New code</Button>
          </div>
          {promos.length === 0 ? (
            <Card className="p-10 text-center">
              <Ticket size={36} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-600 font-medium">No promo codes yet</p>
              <p className="text-xs text-gray-400 mt-1">Create one — then attach it to a Send a Deal email.</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {promos.map(p => (
                <Card key={p.id} className="p-4 flex items-center gap-3" data-testid={`promo-row-${p.id}`}>
                  <div className="bg-amber-100 text-amber-800 px-3 py-2 rounded-lg font-mono font-bold tracking-wider">
                    {p.code}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">
                      {p.discount_type === 'percent' ? `${p.discount_value}% off` : `$${p.discount_value} off`}
                    </p>
                    <p className="text-xs text-gray-500">
                      {p.current_uses || 0} use(s){p.max_uses ? ` / ${p.max_uses} max` : ''}
                      {p.description ? ` · ${p.description}` : ''}
                    </p>
                  </div>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => handleDeletePromo(p.id)}
                    className="text-red-500 border-red-200 hover:bg-red-50"
                  ><Trash2 size={14} /></Button>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {view === 'broadcast' && (
        <div>
          <Card className="p-3 mb-3 bg-purple-50 border-purple-200">
            <p className="text-xs text-purple-800">
              <Megaphone size={12} className="inline mr-1" />
              One-off branded email blast. Pick existing clients OR enter custom emails, draft your message, attach a promo if you like, hit send.
            </p>
          </Card>
          <div className="flex justify-end mb-3">
            <Button
              onClick={() => setDealModal({
                subject: '', body_html: '<p>Hi there,</p>\n<p>We have a special deal for you...</p>',
                promo_code: '', custom_emails: '',
              })}
              className="bg-purple-600 hover:bg-purple-500"
              data-testid="compose-deal-btn"
            ><Send size={16} className="mr-1" /> Compose deal</Button>
          </div>
          <Card className="p-4">
            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
              Pick client recipients ({selectedClientIds.length} selected)
            </p>
            {clients.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No clients in the system yet.</p>
            ) : (
              <div className="max-h-60 overflow-y-auto border rounded">
                {clients.map(c => (
                  <label key={c.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0">
                    <input
                      type="checkbox"
                      checked={selectedClientIds.includes(c.id)}
                      onChange={(e) => setSelectedClientIds(e.target.checked
                        ? [...selectedClientIds, c.id]
                        : selectedClientIds.filter(id => id !== c.id))}
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{c.name || c.contact_name || c.email}</p>
                      <p className="text-xs text-gray-500">{c.email}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Promo modal */}
      <Dialog open={!!promoModal} onOpenChange={(open) => !open && setPromoModal(null)}>
        <DialogContent data-testid="promo-modal">
          <DialogHeader><DialogTitle>New Promo Code</DialogTitle></DialogHeader>
          {promoModal && (
            <div className="space-y-3">
              <div><Label>Code *</Label><Input value={promoModal.code} onChange={(e) => setPromoModal({ ...promoModal, code: e.target.value.toUpperCase() })} placeholder="SUMMER25" /></div>
              <div><Label>Description</Label><Input value={promoModal.description} onChange={(e) => setPromoModal({ ...promoModal, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Discount type</Label>
                  <select
                    value={promoModal.discount_type}
                    onChange={(e) => setPromoModal({ ...promoModal, discount_type: e.target.value })}
                    className="w-full h-10 px-3 border border-input bg-background rounded-md text-sm"
                  >
                    <option value="percent">Percent (%)</option>
                    <option value="amount">Amount ($)</option>
                  </select>
                </div>
                <div><Label>Value</Label><Input type="number" min={0} step="0.01" value={promoModal.discount_value} onChange={(e) => setPromoModal({ ...promoModal, discount_value: e.target.value })} /></div>
              </div>
              <div><Label>Max uses (optional)</Label><Input type="number" min={1} value={promoModal.max_uses} onChange={(e) => setPromoModal({ ...promoModal, max_uses: e.target.value })} /></div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPromoModal(null)}>Cancel</Button>
                <Button onClick={handleSavePromo} className="bg-purple-600 hover:bg-purple-500" data-testid="promo-save">Create</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Deal modal */}
      <Dialog open={!!dealModal} onOpenChange={(open) => !open && !sending && setDealModal(null)}>
        <DialogContent className="max-w-2xl" data-testid="deal-modal">
          <DialogHeader><DialogTitle>Compose Deal Email</DialogTitle></DialogHeader>
          {dealModal && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500">
                Sending to <b>{selectedClientIds.length}</b> selected client(s)
                {selectedClientIds.length === 0 && ' (or add custom emails below).'}
              </p>
              {selectedClientIds.length === 0 && (
                <div>
                  <Label>Custom emails (comma- or newline-separated)</Label>
                  <Textarea
                    rows={2}
                    value={dealModal.custom_emails}
                    onChange={(e) => setDealModal({ ...dealModal, custom_emails: e.target.value })}
                    placeholder="alice@acme.com, bob@beta.com"
                  />
                </div>
              )}
              <div><Label>Subject *</Label><Input value={dealModal.subject} onChange={(e) => setDealModal({ ...dealModal, subject: e.target.value })} placeholder="Exclusive offer just for you" /></div>
              <div>
                <Label>Body HTML *</Label>
                <Textarea
                  rows={10}
                  value={dealModal.body_html}
                  onChange={(e) => setDealModal({ ...dealModal, body_html: e.target.value })}
                />
              </div>
              <div>
                <Label>Attach promo code (optional)</Label>
                <select
                  value={dealModal.promo_code}
                  onChange={(e) => setDealModal({ ...dealModal, promo_code: e.target.value })}
                  className="w-full h-10 px-3 border border-input bg-background rounded-md text-sm"
                >
                  <option value="">— no promo —</option>
                  {promos.map(p => <option key={p.id} value={p.code}>{p.code} ({p.discount_type === 'percent' ? `${p.discount_value}%` : `$${p.discount_value}`} off)</option>)}
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" disabled={sending} onClick={() => setDealModal(null)}>Cancel</Button>
                <Button
                  onClick={handleSendDeal}
                  disabled={sending}
                  className="bg-purple-600 hover:bg-purple-500"
                  data-testid="deal-send"
                >
                  {sending ? <Loader2 className="animate-spin mr-1" size={14} /> : <Send size={14} className="mr-1" />}
                  Send now
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================
// SECTION 4: Portfolio Photos
// ============================================================
// Auto-attached to every "Single Business Order Form" lead-intake
// auto-reply. Admin uploads 3-5 photos here once; every new lead gets
// them inline in the response email + as downloadable attachments.

const PortfolioPhotosSection = () => {
  const [photos, setPhotos] = useState([]);
  const [attachLimit, setAttachLimit] = useState(7);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const fetchPhotos = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/marketing/portfolio-photos`, auth());
      setPhotos(res.data.photos || []);
      if (res.data.attach_limit) setAttachLimit(res.data.attach_limit);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load photos');
    } finally { setLoading(false); }
  };

  const saveAttachLimit = async (limit) => {
    const prev = attachLimit;
    setAttachLimit(limit);
    try {
      await axios.put(`${API}/admin/marketing/portfolio-photos/attach-limit`, { limit }, auth());
      toast.success(`Lead replies will now attach the first ${limit} photo${limit === 1 ? '' : 's'}.`);
    } catch (e) {
      setAttachLimit(prev);
      toast.error(e.response?.data?.detail || 'Failed to update attach limit');
    }
  };

  useEffect(() => { fetchPhotos(); }, []);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let uploaded = 0;
    let failed = 0;
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        await axios.post(`${API}/admin/marketing/portfolio-photos`, fd, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            // axios sets multipart boundary automatically when content-type is omitted
          },
        });
        uploaded += 1;
      } catch (e) {
        failed += 1;
        toast.error(e.response?.data?.detail || `Upload failed for ${file.name}`);
      }
    }
    if (uploaded) toast.success(`Uploaded ${uploaded} photo${uploaded === 1 ? '' : 's'}`);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setUploading(false);
    fetchPhotos();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this portfolio photo? It will stop appearing in lead-intake auto-replies.')) return;
    try {
      await axios.delete(`${API}/admin/marketing/portfolio-photos/${id}`, auth());
      toast.success('Photo deleted');
      fetchPhotos();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div data-testid="portfolio-section">
      <Card className="p-4 mb-4 bg-purple-50 border-purple-200">
        <p className="text-sm text-purple-900">
          <Megaphone size={14} className="inline mr-1.5 -mt-0.5" />
          <b>Auto-attached to every lead-intake reply.</b> Upload your best campaign photos. The first {attachLimit} (in display order) are attached inline to every "Single Business Order Form" auto-reply email. Max 10 photos, 8 MB each. JPG / PNG / WEBP / GIF.
        </p>
      </Card>

      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <p className="text-sm text-gray-600">
          {photos.length} of 10 photos · {photos.filter((_, i) => i < attachLimit).length} attached to next reply
        </p>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-gray-600">
            Attach first
            <select
              value={attachLimit}
              onChange={(e) => saveAttachLimit(parseInt(e.target.value, 10))}
              className="h-8 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
              data-testid="portfolio-attach-limit-select"
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            photos to lead replies
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="portfolio-upload-input"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || photos.length >= 10}
            className="bg-purple-600 hover:bg-purple-500"
            data-testid="portfolio-upload-btn"
          >
            {uploading
              ? <><Loader2 size={14} className="mr-1 animate-spin" /> Uploading…</>
              : <><Upload size={14} className="mr-1" /> Upload photos</>
            }
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-12 flex justify-center"><Loader2 className="animate-spin text-purple-500" /></div>
      ) : photos.length === 0 ? (
        <Card className="p-12 text-center">
          <ImageIcon size={42} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-600 font-medium">No portfolio photos yet</p>
          <p className="text-xs text-gray-400 mt-1">
            Upload showcase photos of previous door hanger campaigns — they'll auto-attach to every new lead reply.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {photos.map((p, idx) => (
            <Card key={p.id} className="p-2 group relative overflow-hidden" data-testid={`portfolio-photo-${p.id}`}>
              <div className="aspect-square overflow-hidden rounded bg-gray-100">
                <img
                  src={`${BACKEND_URL}${p.url}`}
                  alt={p.original_name}
                  className="w-full h-full object-cover transition-transform group-hover:scale-105"
                />
              </div>
              <div className="absolute top-3 left-3">
                {idx < attachLimit ? (
                  <Badge className="bg-emerald-500 text-white text-[10px] shadow-sm">
                    <CheckIcon /> #{idx + 1} attached
                  </Badge>
                ) : (
                  <Badge className="bg-gray-400 text-white text-[10px] shadow-sm">
                    Slot #{idx + 1}
                  </Badge>
                )}
              </div>
              <button
                onClick={() => handleDelete(p.id)}
                className="absolute top-3 right-3 p-1.5 rounded-full bg-white/90 hover:bg-red-50 text-red-500 border border-red-200 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                title="Delete photo"
                data-testid={`portfolio-delete-${p.id}`}
              >
                <Trash2 size={14} />
              </button>
              <p className="text-xs text-gray-500 mt-1.5 truncate" title={p.original_name}>
                {p.original_name}
              </p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const CheckIcon = () => (
  <svg viewBox="0 0 12 12" width="10" height="10" className="inline mr-0.5" fill="currentColor">
    <path d="M10 3L4.5 8.5 2 6l1-1 1.5 1.5L9 2l1 1z" />
  </svg>
);

// ============================================================
// SECTION 7: Sent Emails viewer — all outgoing marketing email
// ============================================================
// Reads the audit rows in `marketing_email_sends`. Three kinds:
//   lead_intake_reply — Single Business Order Form auto-replies
//   sequence_step     — drip-sequence step sends
//   send_deal         — one-off "Send a Deal" broadcasts (1 row per blast)
// Each row carries the full HTML body so the admin can see EXACTLY
// what the customer received (rendered in a sandboxed iframe).

const SEND_KIND_META = {
  lead_intake_reply: { label: 'Order Form', badge: 'bg-purple-100 text-purple-700 hover:bg-purple-100' },
  sequence_step: { label: 'Sequence', badge: 'bg-sky-100 text-sky-700 hover:bg-sky-100' },
  send_deal: { label: 'Deal Blast', badge: 'bg-amber-100 text-amber-800 hover:bg-amber-100' },
};

const KIND_FILTERS = [
  { key: '', label: 'All' },
  { key: 'lead_intake_reply', label: 'Order Form' },
  { key: 'sequence_step', label: 'Sequences' },
  { key: 'send_deal', label: 'Deal Blasts' },
];

const SentEmailsSection = () => {
  const [sends, setSends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [openSend, setOpenSend] = useState(null); // full detail incl. body_html
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showRecipients, setShowRecipients] = useState(false);

  const fetchSends = async (q = '', kind = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('search', q);
      if (kind) params.set('kind', kind);
      const qs = params.toString();
      const r = await axios.get(`${API}/admin/marketing/email-sends${qs ? `?${qs}` : ''}`, auth());
      setSends(r.data.sends || []);
    } catch (e) {
      toast.error('Failed to load sent emails');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSends(); }, []);

  const changeKind = (k) => {
    setKindFilter(k);
    fetchSends(search.trim(), k);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchSends(search.trim(), kindFilter);
  };

  const openDetail = async (send) => {
    setShowRecipients(false);
    setLoadingDetail(true);
    setOpenSend({ ...send, body_html: null }); // placeholder while we fetch
    try {
      const r = await axios.get(`${API}/admin/marketing/email-sends/${send.id}`, auth());
      setOpenSend(r.data);
    } catch (e) {
      toast.error('Failed to load email body');
      setOpenSend(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  return (
    <div data-testid="sent-emails-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Inbox size={18} /> Sent Emails
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Every marketing email that left the system — order-form auto-replies,
            drip-sequence steps, and "Send a Deal" broadcasts. Click any row to
            read the exact email the customer received.
          </p>
        </div>
      </div>

      <div className="flex gap-1.5 mb-4" data-testid="sent-emails-kind-filters">
        {KIND_FILTERS.map(f => (
          <button
            key={f.key || 'all'}
            onClick={() => changeKind(f.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
              kindFilter === f.key
                ? 'bg-purple-600 border-purple-600 text-white'
                : 'bg-white border-gray-200 text-gray-600 hover:border-purple-300 hover:text-purple-700'
            }`}
            data-testid={`sent-emails-kind-${f.key || 'all'}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <form onSubmit={handleSearch} className="flex gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Filter by email (e.g. jane@example.com)"
            className="pl-9"
            data-testid="sent-emails-search-input"
          />
        </div>
        <Button type="submit" variant="outline" data-testid="sent-emails-search-btn">
          Search
        </Button>
        {search && (
          <Button
            type="button" variant="ghost"
            onClick={() => { setSearch(''); fetchSends('', kindFilter); }}
            data-testid="sent-emails-clear-btn"
          >
            Clear
          </Button>
        )}
      </form>

      {loading ? (
        <div className="text-center py-12 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading…
        </div>
      ) : sends.length === 0 ? (
        <Card className="p-8 text-center text-gray-500" data-testid="sent-emails-empty">
          <Mail className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p className="font-medium">No sent emails on record yet.</p>
          <p className="text-xs mt-1">
            {search || kindFilter
              ? 'No matches for that filter. Clear the search or switch to "All".'
              : 'Order-form auto-replies, sequence sends, and deal blasts land here.'}
          </p>
        </Card>
      ) : (
        <div className="border rounded-lg overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium">Sent</th>
                <th className="px-4 py-2.5 text-left font-medium">Type</th>
                <th className="px-4 py-2.5 text-left font-medium">To</th>
                <th className="px-4 py-2.5 text-left font-medium">Subject</th>
                <th className="px-4 py-2.5 text-center font-medium">Status</th>
                <th className="px-4 py-2.5 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sends.map(s => {
                const meta = SEND_KIND_META[s.kind] || { label: s.kind || '?', badge: 'bg-gray-100 text-gray-700 hover:bg-gray-100' };
                return (
                <tr
                  key={s.id}
                  className="border-t hover:bg-gray-50 cursor-pointer"
                  onClick={() => openDetail(s)}
                  data-testid={`sent-email-row-${s.id}`}
                >
                  <td className="px-4 py-2.5 text-gray-700 whitespace-nowrap">
                    {new Date(s.sent_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge className={meta.badge} data-testid={`sent-email-kind-${s.id}`}>{meta.label}</Badge>
                  </td>
                  <td className="px-4 py-2.5">
                    {s.kind === 'send_deal' ? (
                      <div className="text-gray-900 font-medium">
                        {s.recipient_count ?? 0} recipient{(s.recipient_count ?? 0) === 1 ? '' : 's'}
                        {s.recipient_type && <span className="text-xs text-gray-500 font-normal"> · {s.recipient_type}</span>}
                      </div>
                    ) : (
                      <>
                        <div className="text-gray-900 font-medium">{s.lead_email}</div>
                        {s.kind === 'sequence_step' ? (
                          <div className="text-xs text-gray-500">
                            {s.sequence_name ? `${s.sequence_name} · ` : ''}Step {(s.step_index ?? 0) + 1}
                          </div>
                        ) : (
                          s.lead_name && <div className="text-xs text-gray-500">{s.lead_name}</div>
                        )}
                      </>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-gray-700 max-w-xs truncate" title={s.subject}>
                    {s.subject || <span className="text-gray-400 italic">(no subject stored)</span>}
                    {s.kind === 'lead_intake_reply' && (s.attachment_count ?? 0) > 0 && (
                      <span className="ml-2 text-xs text-gray-400">
                        <ImageIcon size={11} className="inline -mt-0.5" /> {s.attachment_count}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    {s.kind === 'send_deal' ? (
                      <Badge className={(s.failed_count ?? 0) > 0
                        ? 'bg-amber-100 text-amber-800 hover:bg-amber-100'
                        : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100'}
                      >
                        {s.delivered_count ?? 0}/{s.recipient_count ?? 0} delivered
                      </Badge>
                    ) : s.delivered ? (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                        Delivered
                      </Badge>
                    ) : (
                      <Badge
                        className="bg-rose-100 text-rose-700 hover:bg-rose-100"
                        title={s.error || ''}
                      >
                        Failed
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Button
                      size="sm" variant="ghost"
                      onClick={(e) => { e.stopPropagation(); openDetail(s); }}
                      data-testid={`sent-email-open-${s.id}`}
                    >
                      <Eye size={14} className="mr-1" /> Read
                    </Button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail modal — renders the actual HTML in a sandboxed iframe */}
      <Dialog open={!!openSend} onOpenChange={(open) => { if (!open) setOpenSend(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="sent-email-detail-dialog">
          <DialogHeader>
            <DialogTitle>
              {openSend
                ? `${(SEND_KIND_META[openSend.kind] || { label: 'Sent' }).label} email`
                : 'Sent email'}
            </DialogTitle>
          </DialogHeader>
          {openSend && (
            <div className="flex-1 overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm mb-4 bg-gray-50 p-4 rounded">
                {openSend.kind === 'send_deal' ? (
                  <>
                    <div>
                      <span className="text-gray-500">Recipients:</span>{' '}
                      <b>{openSend.recipient_count ?? 0}</b>
                      {openSend.recipient_type && <span className="text-gray-500 text-xs"> ({openSend.recipient_type})</span>}
                    </div>
                    <div>
                      <span className="text-gray-500">Status:</span>{' '}
                      <Badge className={(openSend.failed_count ?? 0) > 0
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-emerald-100 text-emerald-700'}
                      >
                        {openSend.delivered_count ?? 0}/{openSend.recipient_count ?? 0} delivered
                      </Badge>
                      {(openSend.failed_count ?? 0) > 0 && (
                        <span className="text-xs text-rose-600 ml-2">{openSend.failed_count} failed</span>
                      )}
                    </div>
                    {openSend.promo_code && (
                      <div>
                        <span className="text-gray-500">Promo code:</span>{' '}
                        <Badge className="bg-orange-100 text-orange-800">{openSend.promo_code}</Badge>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div><span className="text-gray-500">To:</span> <b>{openSend.lead_email}</b></div>
                    <div>
                      <span className="text-gray-500">Status:</span>{' '}
                      {openSend.delivered ? (
                        <Badge className="bg-emerald-100 text-emerald-700">Delivered</Badge>
                      ) : (
                        <Badge className="bg-rose-100 text-rose-700">Failed</Badge>
                      )}
                    </div>
                  </>
                )}
                {openSend.kind === 'sequence_step' && (
                  <div className="sm:col-span-2">
                    <span className="text-gray-500">Sequence:</span>{' '}
                    {openSend.sequence_name || <em className="text-gray-400">(unnamed)</em>}
                    {' · '}Step {(openSend.step_index ?? 0) + 1}
                  </div>
                )}
                {openSend.kind === 'lead_intake_reply' && (
                  <>
                    <div><span className="text-gray-500">From:</span> {openSend.from_email || '—'}</div>
                    <div><span className="text-gray-500">BCC:</span> {openSend.bcc || '—'}</div>
                  </>
                )}
                <div className="sm:col-span-2">
                  <span className="text-gray-500">Subject:</span> {openSend.subject || <em className="text-gray-400">(none)</em>}
                </div>
                <div className="sm:col-span-2">
                  <span className="text-gray-500">Sent at:</span> {new Date(openSend.sent_at).toLocaleString()}
                </div>
                {openSend.error && (
                  <div className="sm:col-span-2 text-rose-700 text-xs">
                    <span className="font-medium">Error:</span> {openSend.error}
                  </div>
                )}
                {openSend.kind === 'lead_intake_reply' && (
                  <div className="sm:col-span-2 text-xs text-gray-500">
                    {openSend.attachment_count ?? 0} portfolio photo(s) attached ·
                    CTA: <a href={openSend.cta_url} target="_blank" rel="noopener noreferrer" className="text-purple-700 underline break-all">{openSend.cta_url}</a>
                  </div>
                )}
                {openSend.kind === 'send_deal' && Array.isArray(openSend.recipient_emails) && openSend.recipient_emails.length > 0 && (
                  <div className="sm:col-span-2">
                    <button
                      type="button"
                      onClick={() => setShowRecipients(v => !v)}
                      className="text-xs text-purple-700 underline"
                      data-testid="sent-email-toggle-recipients"
                    >
                      {showRecipients ? 'Hide' : 'Show'} {openSend.recipient_emails.length} recipient{openSend.recipient_emails.length === 1 ? '' : 's'}
                    </button>
                    {showRecipients && (
                      <div
                        className="mt-2 max-h-40 overflow-y-auto flex flex-wrap gap-1.5 p-2 bg-white border rounded"
                        data-testid="sent-email-recipient-list"
                      >
                        {openSend.recipient_emails.map(em => (
                          <span key={em} className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full">
                            {em}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="border rounded overflow-hidden bg-white">
                {loadingDetail || openSend.body_html === null ? (
                  <div className="p-8 text-center text-gray-400">
                    <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading email body…
                  </div>
                ) : openSend.body_html ? (
                  <iframe
                    title="Email body preview"
                    srcDoc={openSend.body_html}
                    sandbox=""
                    className="w-full h-[520px] border-0 bg-white"
                    data-testid="sent-email-body-iframe"
                  />
                ) : (
                  <div className="p-8 text-center text-gray-400 italic">
                    Body was not captured for this send.
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMarketing;
